import React, {memo, useCallback, useState} from 'react';
import './index.scss';

import iconInfo from '../../assets/images/icons/pop-up/media.png';
import iconFailure from '../../assets/images/icons/pop-up/cancel.png';
import iconSuccess from '../../assets/images/icons/pop-up/ok.png';
import iconLoading from '../../assets/images/icons/pop-up/loading.gif';
import iconWarning from '../../assets/images/icons/attention.png';
import {closePopupScreenAction} from '../../store/ui/actions';
import {useDispatch, useSelector} from 'react-redux';
import {useOutsideDetector} from '../../hooks/outside-detector';
import {onClickHandler} from '../../utils/onclick';
import {useOrientation} from '../../hooks/orientation';
import {useInterval} from '../../hooks/interval';
import {useLocalization} from "../../hooks/localization";
import {SettingCheckBoxComponent} from "../../components/Settings/SettingCheckboxComponent";


export const PopupScreen = memo(function PopupScreen() {
    const {t} = useLocalization();
    const popupState = useSelector(state => state.ui.popupState);
    const dispatch = useDispatch();
    const orientation = useOrientation();
    let {type, icon, message, callback, positive_button, showCancel, showRememberSelection, checkboxInfo} = popupState;
    const [checkboxValue, setCheckboxValue] = useState(false);

    const centerAreaRef = useOutsideDetector(type !== 'question' && popupState.closable, () => {
        dispatch(closePopupScreenAction());
    });

    const [remainingTime, setRemainingTime] = useState(positive_button.timed ? 3000 : 0);

    const clearInt = useInterval(() => {
        setRemainingTime(r => r - 1000);
        if (remainingTime <= 0) {
            clearInt();
        }
    }, 1000);

    if (icon == null) {
        if (type === 'info') {
            icon = iconInfo;
        } else if (type === 'failure') {
            icon = iconFailure;
        } else if (type === 'success') {
            icon = iconSuccess;
        }else if (type === 'loading') {
            icon = iconLoading;
        } else if (type === 'warning') {
            icon = iconWarning;
        }
    }

    const answer = useCallback((isApproved: boolean) => {
        dispatch(closePopupScreenAction());
        if (callback) {
            callback(isApproved, checkboxValue);
        }
    }, [dispatch, callback, checkboxValue]);

    const onPositiveButtonClick = useCallback(() => {
        if (remainingTime > 0) {
            return;
        }

        answer(true);
    }, [remainingTime, answer]);

    const onCancelClick = useCallback(() => {
        dispatch(closePopupScreenAction());
    }, [dispatch]);

    const handleCheckboxToggle = useCallback(() => {
        setCheckboxValue(!checkboxValue);
    }, [checkboxValue]);

    const getButtonText = () => {
        if (typeof positive_button.text === 'function') {
            return positive_button.text(checkboxValue);
        }
        return positive_button.text || t('YES');
    };

    return (
        <div className={'popup-screen-wrapper ' + orientation}>
            {
                renderIf(type !== 'question')(
                    () =>
                        <div className={type} ref={centerAreaRef}>
                            <img src={icon} alt=""/>
                            <span>{message}</span>
                            {showRememberSelection && (
                                <div className="checkbox-container">
                                    <SettingCheckBoxComponent 
                                        label={t('')} 
                                        set={handleCheckboxToggle} 
                                        value={checkboxValue} 
                                    />
                                    {checkboxInfo && <div className="checkbox-info">{checkboxInfo}</div>}
                                </div>
                            )}
                        </div>,
                    () =>
                        <div className="question" ref={centerAreaRef}>
                            <span>{message}</span>
                            {showRememberSelection && (
                                <div className="checkbox-container">
                                    <SettingCheckBoxComponent 
                                        label={t('')} 
                                        set={handleCheckboxToggle} 
                                        value={checkboxValue} 
                                    />
                                    {checkboxInfo && <div className="checkbox-info">{checkboxInfo}</div>}
                                </div>
                            )}
                            <div className="action-row">
                                <div className="button" {...onClickHandler(() => answer(false))}>
                                    <img src={iconFailure} alt=""/>
                                    <span>{t('NO')}</span>
                                </div>
                                <div
                                    className='button' {...onClickHandler(onPositiveButtonClick)}>
                                    {
                                        renderIf(remainingTime <= 0)(() =>
                                            <img src={iconSuccess} alt=""/>, () =>
                                            <span
                                                className='passive'>{`${Math.ceil(remainingTime / 1000).toString()}`}</span>)
                                    }
                                    <span>{getButtonText()}</span>
                                </div>
                                {
                                    renderIf(showCancel === true)(() => // todo iptal et icon
                                        <div className='button' {...onClickHandler(onCancelClick)}>
                                            <img src={iconFailure} alt=""/>
                                            <span>{t('CANCEL_2')}</span>
                                        </div>,
                                    )
                                }
                            </div>
                        </div>,
                )
            }
        </div>
    );
});
